import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DynamicAttributesDirective } from '../../utils/directives/dynamic-attributes.directive';
import { provideNgxMask, NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'app-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    CommonModule,
    DynamicAttributesDirective,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
})
export class TextFieldComponent implements OnInit, AfterViewInit {
  @Input() inputControl: FormControl = new FormControl('');
  @Input() prefixIconName: string;
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() showCloseButton = false;
  @Input() isPasswordField = false;
  @Input() isNumberField = false;
  @Input() isPriceField: boolean = false;
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() label: string = '';
  @Input() ibanInput = false;
  @Input() readonly = false;
  @Input() inputProps: { [key: string]: any } = {};
  @Input() mask: string;
  @Input() shownMaskExpression: string;
  @Input() showMaskTyped: boolean;
  @Input() prefix: string;
  @Input() hasBorder = false;
  @Input() clearButtonAlwaysVisible = false;
  @ViewChild('inputElement') inputElement: ElementRef;
  hidePassword = true;
  previousValue: string;

  constructor() {}

  ngOnInit(): void {
    if (this.isPriceField) {
      this.inputControl.valueChanges.subscribe(() => {
        this.adjustEuroSignPosition();
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.isPriceField) {
      this.adjustEuroSignPosition();
    }
  }

  adjustEuroSignPosition(): void {
    if (!this.inputElement) return;
    const valueLength = this.inputControl.value
      ? this.inputControl.value.toString().length
      : 0;
    const charWidth = 8;
    const euroSignElement = this.inputElement.nativeElement.nextElementSibling;
    euroSignElement.style.left = `calc(50% - ${(valueLength * charWidth) / 2 + 15}px)`;
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    if (this.isNumberField) {
      const allowedChars = '0123456789.,';
      const inputChar = String.fromCharCode(event.charCode);
      if (!allowedChars.includes(inputChar)) {
        event.preventDefault();
      }
    }
  }

  onBlur(value: string): void {
    if (!this.isNumberField) return;
    if (value !== null && value !== undefined && value !== '') {
      let numericValue = parseFloat(value.replace(',', '.'));
      const parsedMax = parseFloat(this.inputProps.max);
      const parsedMin = parseFloat(this.inputProps.min);
      if (isNaN(numericValue) && this.inputProps.maxInput !== undefined) {
        numericValue = parsedMax;
      }
      if (isNaN(numericValue) && this.inputProps.minInput !== undefined) {
        numericValue = parsedMin;
      }
      if (this.inputProps.max !== undefined && numericValue > parsedMax) {
        numericValue = parsedMax;
      }
      if (this.inputProps.min !== undefined && numericValue < parsedMin) {
        numericValue = parsedMin;
      }
      const formattedValue = numericValue.toFixed(2);
      this.inputProps.onPriceChange(
        this.inputProps.maxInput ? 'max' : 'min',
        formattedValue
      );
      this.inputControl.setValue(formattedValue, { emitEvent: false });
    }
    if (!this.inputControl.value.trim()) {
      this.inputControl.setValue(this.previousValue);
    }
  }

  clearInput(): void {
    if (this.inputProps.onClear) {
      this.inputProps.onClear();
    }
    this.inputControl.setValue('');
  }

  isInputInvalid() {
    return (
      this.inputControl.invalid &&
      (this.inputControl.dirty || this.inputControl.touched)
    );
  }

  getErrorMessage(): string | undefined {
    const errors = this.inputControl.errors;
    if (!errors) return undefined;

    const errorKey = Object.keys(errors)[0];
    return this.errorMessages[errorKey];
  }

  togglePasswordVisibility() {
    const newType = this.inputProps['type'] === 'text' ? 'password' : 'text';
    this.inputProps = { ...this.inputProps, type: newType };
  }

  onFocus() {
    this.previousValue = this.inputControl.value;
    this.inputControl.setValue('');
  }
}
